import { defineStore } from "pinia";

import { PageKind } from "~/shared/enums";
import type { Comment, Dartboard, Dashboard, Doc, Folder, Form, Page, Space, Task, User, View } from "~/shared/types";
import { getPageDisplayName, slugifyStr } from "~/utils/common";

const PREVIEW_PR_NUMBER_RE = /^preview-(\d+)/;

type BackendEnvironment = {
  name: string;
  startedAt: Date;
  branch: string;
  prNumber: number | null;
  prTitle: string | null;
  commitMessage: string;
  hash: string;
  changes: boolean;
};

type State = {
  host?: string;
  origin?: string;
  isLocalStandard: boolean;
  isLocalBuild: boolean;
  isLocalNetwork: boolean;
  isLocal: boolean;
  isStag: boolean;
  isProd: boolean;
  isPreview: boolean;
  isProxy: boolean;
  isPlaywright: boolean;
  isReviewerActive: boolean;
  needsUpdate: boolean;
  backendEnvironment?: BackendEnvironment;
};

const isSecure = (state: State) => !(state.isLocalStandard || state.isLocalNetwork || state.isLocalBuild);

const urlBase = (state: State) => (isSecure(state) ? "https://" : "http://") + state.host;

const useEnvironmentStore = defineStore("EnvironmentStore", {
  state: (): State =>
    ({
      host: undefined,
      origin: undefined,
      isLocalStandard: false,
      isLocalBuild: false,
      isLocalNetwork: false,
      isLocal: false,
      isStag: false,
      isProd: false,
      isPreview: false,
      isProxy: false,
      isPlaywright: false,
      // Manually set to true when the mobile app is being reviewed
      isReviewerActive: false,
      needsUpdate: false,
      backendEnvironment: undefined,
    }) satisfies State,
  getters: {
    isSecure,
    isUnknown: (state) => !(state.isLocal || state.isStag || state.isProd || state.isPreview),
    wsBase: (state) => (isSecure(state) ? "wss://" : "ws://") + state.host,
    urlBase,
    name: (state) =>
      state.isLocal ? "local" : state.isStag ? "stag" : state.isProd ? "prod" : state.isPreview ? "preview" : "unknown",
    prettyName: (state) =>
      state.isLocal
        ? `Local${
            state.isLocalBuild && state.backendEnvironment?.hash
              ? ` ${state.backendEnvironment?.hash?.substring(0, 7)}`
              : state.isProxy
                ? " proxy"
                : state.isLocalNetwork
                  ? " network"
                  : ""
          }`
        : state.isStag
          ? "Staging"
          : state.isProd
            ? "Production"
            : state.isPreview
              ? `Preview #${state.host?.match(PREVIEW_PR_NUMBER_RE)?.[1] ?? ""}`
              : "Unknown",
    // TODO there are sorta three copies of these: in EnvironmentStore; in router/common; and in utils/common
    getSpaceUrl: (state) => (space: Space) =>
      `${urlBase(state)}/s/${space.duid}-${slugifyStr(
        getPageDisplayName(space, () => undefined),
        { trim: { len: 30 } }
      )}`,
    getUserUrl: (state) => (user: User) => `${urlBase(state)}/u/${user.duid}`,
    getViewUrl: (state) => (view: View) =>
      `${urlBase(state)}/v/${view.duid}-${slugifyStr(
        getPageDisplayName(view, () => undefined),
        { trim: { len: 30 } }
      )}`,
    getDartboardUrl() {
      return (dartboard: Dartboard) =>
        `${urlBase(this)}/d/${dartboard.duid}-${slugifyStr(
          getPageDisplayName(dartboard, this.$useDataStore().getSpaceByDuid),
          { trim: { len: 30 } }
        )}`;
    },
    getTaskUrl: (state) => (task: Task | { duid: string; title: string }) =>
      `${urlBase(state)}/t/${task.duid}-${slugifyStr(task.title, { trim: { len: 30 } })}`,
    getCommentUrl: (state) => (comment: Comment) => `${urlBase(state)}/c/${comment.duid}`,
    getFolderUrl: (state) => (folder: Folder) =>
      `${urlBase(state)}/f/${folder.duid}-${slugifyStr(
        getPageDisplayName(folder, () => undefined),
        { trim: { len: 30 } }
      )}`,
    getFormUrl: (state) => (form: Form) =>
      `${urlBase(state)}/p/r/${form.duid}-${slugifyStr(form.title, { trim: { len: 30 } })}`,
    getDocUrl: (state) => (doc: Doc) =>
      `${urlBase(state)}/o/${doc.duid}-${slugifyStr(doc.title, { trim: { len: 30 } })}`,
    getHomeUrl: (state) => () => `${urlBase(state)}/home`,
    getInboxUrl: (state) => () => `${urlBase(state)}/inbox`,
    getAgentsUrl: (state) => () => `${urlBase(state)}/agents`,
    getAgentUrlByKind: (state) => (kind: string) => `${urlBase(state)}/agents?kind=${encodeURIComponent(kind)}`,
    getViewsRootUrl: (state) => () => `${urlBase(state)}/views`,
    getDashboardUrl: (state) => (dashboard: Dashboard) =>
      `${urlBase(state)}/b/${dashboard.duid}-${slugifyStr(dashboard.title, { trim: { len: 30 } })}`,
    getDashboardsRootUrl: (state) => () => `${urlBase(state)}/dashboards`,
    getPageUrl() {
      return (page: Page) => {
        const { pageKind } = page;
        switch (pageKind) {
          case PageKind.AGENTS: {
            return this.getAgentsUrl();
          }
          case PageKind.DARTBOARD: {
            return this.getDartboardUrl(page);
          }
          case PageKind.DASHBOARD: {
            return this.getDashboardUrl(page);
          }
          case PageKind.DASHBOARDS_ROOT: {
            return this.getDashboardsRootUrl();
          }
          case PageKind.DOC: {
            return this.getDocUrl(page);
          }
          case PageKind.FOLDER: {
            return this.getFolderUrl(page);
          }
          case PageKind.HOME: {
            return this.getHomeUrl();
          }
          case PageKind.INBOX: {
            return this.getInboxUrl();
          }
          case PageKind.SPACE: {
            return this.getSpaceUrl(page);
          }
          case PageKind.VIEW: {
            return this.getViewUrl(page);
          }
          case PageKind.VIEWS_ROOT: {
            return this.getViewsRootUrl();
          }
          default: {
            throw new Error(`Unknown page kind: ${pageKind}`);
          }
        }
      };
    },
    getSettingsUrl: (state) => (settings: string) => `${urlBase(state)}/?settings=${settings}`,
    getPropertySettingsUrl: (state) => (propertyDuid: string) =>
      `${urlBase(state)}/?settings=properties&property=${propertyDuid}`,
    getPropertySettingsByKindUrl: (state) => (propertyKind: string) =>
      `${urlBase(state)}/?settings=properties&kind=${encodeURIComponent(propertyKind)}`,
    getFormSettingsUrl: (state) => (formDuid: string) => `${urlBase(state)}/?settings=forms&form=${formDuid}`,
    getAutomationSettingsUrl: (state) => (automationDuid: string) =>
      `${urlBase(state)}/?settings=automations&automation=${automationDuid}`,
    getTaskKindSettingsUrl: (state) => (taskKindDuid: string) =>
      `${urlBase(state)}/?settings=types&type=${taskKindDuid}`,
    getSkillSettingsUrl: (state) => (skillDuid: string) => `${urlBase(state)}/?settings=skills&skill=${skillDuid}`,
    getSkillSettingsByKindUrl: (state) => (skillKind: string) =>
      `${urlBase(state)}/?settings=skills&kind=${encodeURIComponent(skillKind)}`,
    getSpaceSettingsUrl:
      (state) =>
      (spaceDuid: string, subpage: string = "basics") =>
        `${urlBase(state)}/?settings=spaces&space=${spaceDuid}&page=${subpage}`,
    samlEntityId: (state) => `${urlBase(state)}/sso/saml`,
    samlAcsUrl(): string {
      const tenantStore = this.$useTenantStore();
      return `${urlBase(this)}/sso/saml/${tenantStore.duid}`;
    },
    scimUrl: (state) => `${urlBase(state)}/scim/v2`,
    googleRedirectUri(): string {
      return `${this.origin}/google-oauth`;
    },
    previewPrNumber: (state) => state.host?.match(PREVIEW_PR_NUMBER_RE)?.[1] ?? null,
    hideForReviewer(): boolean {
      const pageStore = this.$usePageStore();
      return this.isReviewerActive && pageStore.isMobileApp;
    },
  },
  actions: {
    setEnvironment(location: Location) {
      this.host = location.host;
      this.origin = location.origin;

      this.isLocalStandard = this.host.startsWith("localhost:51"); // 5100 for standard run and 5101, 5102 and so on for worktree runs
      this.isLocalBuild = this.host.startsWith("localhost:80"); // 8000 for standard run and 8001, 8002 and so on for worktree runs
      this.isLocalNetwork = this.host.match(/^192\.168\.\d{1,3}\.\d{1,3}:51\d{2}$/) !== null;
      this.isProxy = this.host.startsWith("dart.ngrok.app");
      this.isLocal = this.isLocalStandard || this.isLocalBuild || this.isProxy || this.isLocalNetwork;
      this.isStag = this.host.startsWith("stag.dartai.com");
      this.isProd = this.host.startsWith("app.dartai.com");
      this.isPreview = this.host.match(/^preview-\d+\.dartai\.com$/) !== null;
      this.isPlaywright = !!__PLAYWRIGHT__;
    },
    setBackendEnvironment(backendEnvironment?: BackendEnvironment) {
      this.backendEnvironment = backendEnvironment
        ? { ...backendEnvironment, startedAt: new Date(backendEnvironment.startedAt) }
        : undefined;
    },
    async checkForUpdate() {
      const { data }: { data: BackendEnvironment } = await this.$backendOld.auth.getEnvironment();
      // An empty hash indicates the requesting a new backend pod while the rolling update of the backend deployment in Kubernetes is still ongoing
      if (!data.hash || data.hash === this.backendEnvironment?.hash) {
        return;
      }
      this.needsUpdate = true;
    },
  },
});

export default useEnvironmentStore;
